// useTeamJoin (contract 06 §9) — owns the invited-user form, fixed-copy error state, URL hygiene,
// duplicate-submit guard, and the two route side-effects. HTTP/bootstrap remain in the flow.
import { reactive, ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { AuthCheckPermissionFlow } from '../flows/AuthCheckPermissionFlow'
import { AuthTeamJoinFlow } from '../flows/AuthTeamJoinFlow'
import type { ChangePasswordPayloadV2 } from '../models/auth.types'

import { useAccount } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

export interface UseTeamJoin {
  user: ChangePasswordPayloadV2
  email: Readonly<Ref<string | undefined>>
  pending: Ref<boolean>
  error: Ref<string | null>
  submit: () => Promise<void>
}

export function useTeamJoin(): UseTeamJoin {
  const route = useRoute()
  const router = useRouter()
  const toast = useToast()
  const { t } = useI18n()
  const account = useAccount()
  const flow = new AuthTeamJoinFlow()
  const permission = new AuthCheckPermissionFlow()

  const user = reactive<ChangePasswordPayloadV2>({
    name: '',
    password: '',
    confirm_password: '',
  })
  const pending = ref(false)
  const error = ref<string | null>(null)

  // ORP-21656 AC 7 — read ONCE at setup, before the `?message` scrub below rewrites the query.
  // The decision is the flow's; this composable only navigates to what it returns.
  const landing = permission.resolveInviteLanding(route.query.is_mcp_only)

  const message = route.query.message
  if (permission.showInviteExpiredAlert(message)) {
    toast.warning(t('auth.invite.expired'))
    const { message: _message, ...retainedQuery } = route.query
    void router.replace({ query: retainedQuery })
  }

  watch(
    account.user,
    (currentUser) => {
      if (permission.redirectIfUserHasPassword(currentUser)) {
        void router.replace(landing)
      }
    },
    { immediate: true },
  )

  async function submit(): Promise<void> {
    if (pending.value) return
    pending.value = true
    error.value = null
    try {
      await flow.submitPassword({ ...user })
      await router.push(landing)
    } catch {
      error.value = 'auth.teamJoin.error'
    } finally {
      pending.value = false
    }
  }

  return {
    user,
    email: account.userEmail,
    pending,
    error,
    submit,
  }
}
